import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { OpenFeature } from "@openfeature/web-sdk";

export const fetchFeatureFlags = createAsyncThunk(
  "featureFlags/fetchFeatureFlags",
  async (flagNames, { rejectWithValue }) => {
    try {
      const client = OpenFeature.getClient();
      const flagValues = {};

      for (const flagName of flagNames) {
        try {
          const result = await client.getBooleanValue(flagName, false);
          flagValues[flagName] = result;
        } catch (error) {
          console.warn(`Failed to fetch flag ${flagName}:`, error);
          flagValues[flagName] = false;
        }
      }

      return flagValues;
    } catch (error) {
      console.error("Failed to fetch feature flags:", error);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchFeatureFlag = createAsyncThunk(
  "featureFlags/fetchFeatureFlag",
  async ({ flagName, defaultValue = false }, { rejectWithValue }) => {
    try {
      const client = OpenFeature.getClient();
      const value = await client.getBooleanValue(flagName, defaultValue);
      return { flagName, value };
    } catch (error) {
      console.error(`Failed to fetch flag ${flagName}:`, error);
      return rejectWithValue({ flagName, error: error.message });
    }
  },
);

const initialState = {
  flags: {},
  loading: false,
  error: null,
  lastFetched: null,
};

const featureFlagsSlice = createSlice({
  name: "featureFlags",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFlag: (state, action) => {
      const { flagName, value } = action.payload;
      state.flags[flagName] = value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        state.loading = false;
        state.flags = { ...state.flags, ...action.payload };
        state.lastFetched = Date.now();
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch feature flags";
      })
      .addCase(fetchFeatureFlag.fulfilled, (state, action) => {
        const { flagName, value } = action.payload;
        state.flags[flagName] = value;
        state.lastFetched = Date.now();
      })
      .addCase(fetchFeatureFlag.rejected, (state, action) => {
        const { flagName } = action.meta.arg;
        console.error(`Failed to fetch flag ${flagName}`);
        // keep existing value or set to false if not present
        if (!(flagName in state.flags)) {
          state.flags[flagName] = false;
        }
      });
  },
});

export const { clearError, setFlag } = featureFlagsSlice.actions;

export const selectFeatureFlags = (state) => state.featureFlags.flags;
export const selectFeatureFlagsLoading = (state) => state.featureFlags.loading;
export const selectFeatureFlagsError = (state) => state.featureFlags.error;
export const selectFeatureFlag = (flagName) => (state) =>
  state.featureFlags.flags[flagName] || false;

export default featureFlagsSlice.reducer;
